import { Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractListViewService } from '@y42/core/list-view';
import { DateTimeService } from '@y42/date-time';
import {
  AbstractFeatureChildUIModel,
  AbstractFeatureUIModel,
  FeatureAggregationService,
  FeatureChildAggregationService,
} from '@y42/feature';
import { GIT_FOLDER, Y42GitFolder } from '@y42/feature/git';
import { isNotNullish } from '@y42/util/nullish';
import { map, Observable } from 'rxjs';
import { JobDialogData } from '../../api/job-dialog-data.model';
import { AbstractJobService } from '../../api/job.service';
import { JobListViewData, JobStore, JobUIModel } from '../../state';

@Injectable()
export class JobListViewService extends AbstractListViewService<JobListViewData> {
  constructor(
    protected readonly store: JobStore,
    protected readonly dateTimeService: DateTimeService,
    protected readonly jobService: AbstractJobService,
    private readonly featureAggregationService: FeatureAggregationService,
    private readonly featureChildAggregationService: FeatureChildAggregationService,
    @Inject(MAT_DIALOG_DATA) protected readonly data: JobDialogData,
    @Inject(GIT_FOLDER) protected readonly gitFolder: Y42GitFolder,
  ) {
    super(store);
  }

  readonly data$ = this.jobService.jobs$.pipe(map((jobs) => this.getListItemsFrom(jobs)));

  readonly loading$: Observable<boolean> = this.jobService.loading$;

  async toggleStarred(): Promise<void> {
    // does not apply to jobs
  }

  async saveTags(): Promise<void> {
    // does not apply to jobs
  }

  async saveDescription(): Promise<void> {
    // does not apply to jobs
  }

  private getListItemsFrom(jobs: JobUIModel[]): JobListViewData[] {
    if (!this.data) {
      throw new Error('Invalid configuration of JobListViewService. No data arg provided.');
    }
    const jobsMap = new Map(jobs.map((job) => [job.request_id, job]));
    const data = this.data;

    if (data.parentId !== undefined) {
      if (data.type === 'feature') {
        const featureQuery = this.featureAggregationService.getByGitPath(data.parentId)?.query;
        const feature = featureQuery?.getUIEntity(data.parentId);
        if (!feature?.children) {
          return [];
        }

        return feature.children.map((parent) => this.enhanceJobs(jobsMap, feature, parent)).flat();
      } else if (data.type === 'child') {
        const childQuery = this.featureChildAggregationService.getByGitPath(data.parentId)?.query;
        const child = childQuery?.getUIEntity(data.parentId);
        if (!child) {
          return [];
        }

        const featureQuery = this.featureAggregationService.getByGitPath(child.$parent)?.query;
        const feature = featureQuery?.getUIEntity(child.$parent);
        return feature ? this.enhanceJobs(jobsMap, feature, child) : [];
      } else {
        throw new Error(`Unknown type '${data.type}'`);
      }
    }

    const featureChildCache = new Map<string, AbstractFeatureChildUIModel>();
    const featureCache = new Map<string, AbstractFeatureUIModel>();
    return jobs
      .map((job) => {
        const featureChild =
          featureChildCache.get(job.path) ??
          this.featureChildAggregationService.getByGitPath(job.path)?.query?.getUIEntity(job.path);
        if (!featureChild) {
          return;
        }
        featureChildCache.set(job.path, featureChild);

        const feature =
          featureCache.get(featureChild.$parent) ??
          this.featureAggregationService.getByGitPath(featureChild.$parent)?.query?.getUIEntity(featureChild.$parent);
        if (!feature) {
          return;
        }
        featureCache.set(featureChild.$parent, feature);

        if (data.featureType && data.featureType !== feature.featureType) {
          return;
        }
        return this.enhanceJob(job, feature, featureChild);
      })
      .filter(isNotNullish);
  }

  private enhanceJobs(
    jobs: Map<string, JobUIModel>,
    grandParent: AbstractFeatureUIModel,
    parent: AbstractFeatureChildUIModel,
  ): JobListViewData[] {
    return (parent.$children ?? [])
      .map((jobId) => {
        const job = jobs.get(jobId);
        return job ? this.enhanceJob(job, grandParent, parent) : undefined;
      })
      .filter(isNotNullish);
  }

  private enhanceJob(
    job: JobUIModel,
    grandParent: AbstractFeatureUIModel,
    parent: AbstractFeatureChildUIModel,
  ): JobListViewData {
    return {
      ...job,
      id: job.request_id,
      datasourceName: grandParent.name,
      datasourceTypeName: grandParent.typeLabel || '',
      datasourceTypeLogo: grandParent.typeIcon || '',
      tags: grandParent.tags,
      datasourceTableName: parent.name,
    };
  }
}
